import type { Project, ProjectInfo, StructureType, WallInput } from "./types/input";
import { makeDefaultPricing } from "./quantities";
import { stoneMaterial, type StonePreset } from "./materials";

/** Varsayılan taş duvar: harçlı moloz kalker, B (1:4 çimento) harcı → γ 24 kN/m³ (TS 498), σem 4 kgf/cm² ≈ 392 kN/m² (TS 2510 Çizelge 12) */
export const DEFAULT_STONE_PRESET: StonePreset = { wallType: "moloz", stone: "kalker", mortar: "B" };
export const ENGINE_DEFAULTS = {
  material: { gamma: 24, allowableCompression: 392, jointFriction: 0.6, jointCohesion: 0 },
  water: { gammaWater: 9.81 },
  seismic: { r: 2.0 as const, level: "DD-2" as const, dynamicPointOfApplication: "H/2" as const, includeWallInertia: true, includeBackfillInertia: false, kvOnTotalPressure: true },
  checks: {
    classic: {
      overturning: { static: 1.5, seismic: 1.3 },
      sliding: { static: 1.5, seismic: 1.1 },
      bearing: { static: 1.0, seismic: 1.5 },
      /**
       * Taban dış merkezliliği B/n. Deprem B/3 (Cem 2026-10-01 "yap"): TBDY 16'da hüküm yok (Ac = basınç alanı → kalkma
       * dolaylı kabul); EN 1997-1 §6.5.4 e > B/3'te özel önlem ister, AASHTO LRFD zeminde orta 2/3. Burun basıncını taşıma
       * gücü kontrolü (B' = B − 2e), dengeyi γRdev korur. Eski varsayılan B/4; gövde derzlerinde (jointEccentricity) B/4 kalır.
       */
      eccentricity: { static: 6, seismic: 3 },
      jointShear: { static: 1.5, seismic: 1.1 },
      jointEccentricity: { static: 6, seismic: 4 },
    },
    tbdy: { gammaRdev: 1.3, gammaRdevSeismic: 1.3, gammaRv: 1.4, gammaRh: 1.1, gammaRp: 1.4, passiveShare: 0.3, tanDeltaMax: 0.6 },
  },
  foundation: { bearingRatio: 3.0, passiveNeglectedDepth: 0.5 },
  /** ABD (IBC 2021 §1807.2 / AASHTO LRFD 11.6 ASD pratiği) klasik hedefler: profil seçilince web bunları önerir */
  usClassic: {
    overturning: { static: 1.5, seismic: 1.1 },
    sliding: { static: 1.5, seismic: 1.1 },
    bearing: { static: 1.0, seismic: 1.33 },
    eccentricity: { static: 6, seismic: 3 },
    jointShear: { static: 1.5, seismic: 1.1 },
    jointEccentricity: { static: 6, seismic: 3 },
  },
} as const;

let counter = 0;
export function newId(prefix = "id"): string {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${counter}`;
}

export function makeDefaultWall(name = "Duvar-1", type: StructureType = "masonry-gravity"): WallInput {
  const w = baseWall(name);
  if (type === "masonry-gravity") return w;
  // Betonarme konsol: yaygın oranlar (H = 4,5 m: gövde 0,3/0,5, taban 0,5, burun 0,8, topuk 2,4 → B ≈ 0,8·H); itki topuktan geçen düşey
  // düzlemde Rankine (δ = β); taban sürtünmesi yerinde dökme beton için δ_b = φ (TBDY Tablo 16.3 üst sınırı tanδ ≤ 0,6 motorda uygulanır)
  w.geometry = {
    mode: "rc-cantilever",
    stem: { height: 4.0, topThickness: 0.3, bottomThickness: 0.5, taperSide: "front" },
    base: { thickness: 0.5, toeLength: 0.8, heelLength: 2.4 },
    embedmentDepth: 0.8,
    ...(type === "rc-counterfort" ? { counterforts: { spacing: 3.0, thickness: 0.3, topWidth: 0 } } : {}),
  };
  w.material = { gamma: 25, allowableCompression: 500, jointFriction: 0.6, jointCohesion: 0, description: "Betonarme C25/30 – B420C" };
  w.backfill.wallFrictionAngle = "beta";
  w.foundationSoil.baseFriction = { mode: "phi" };
  w.checks.surchargeAsWeight = false;
  return w;
}

function baseWall(name: string): WallInput {
  return {
    id: newId("wall"),
    name,
    geometry: {
      mode: "parametric",
      frontBatter: 0.2,
      // Cem 2026-09-29: en yaygın tip — ön yüz 5:1 dolguya eğik, arka yüz düşey (alt kalınlık = üst + m·H = 0,60 + 0,2·3)
      steps: [{ height: 3.0, topThickness: 0.6, bottomThickness: 1.2 }],
      // KGM Karayolu Tasarım El Kitabı Şekil 4.15: temel derinliği asgari 1,00 m (docs/research/10). Pasifte üst 0,5 m
      // ihmal edilir (passiveNeglectedDepth) → pasif açılınca D_p = 0,5 m etkir (Df = 0,5 iken D_p = 0 idi, anahtar etkisizdi).
      embedmentDepth: 1.0,
    },
    backfill: {
      layers: [{ name: "Dolgu", thickness: 5, gamma: 18, gammaSat: 20, phi: 30, c: 0 }],
      slopeAngle: 0,
      wallFrictionAngle: "two-thirds-phi",
      permeability: "permeable",
    },
    foundationSoil: {
      gamma: 19, phi: 30, c: 0,
      baseFriction: { mode: "two-thirds-phi" },
      adhesion: 0,
      bearing: { mode: "given", sigmaAllow: 250, ratio: ENGINE_DEFAULTS.foundation.bearingRatio, seismicMethod: "tbdy-16.8a", ec8Category: "auto" },
      passive: { enabled: false, neglectedDepth: ENGINE_DEFAULTS.foundation.passiveNeglectedDepth },
      drained: true,
    },
    water: { backLevel: null, frontLevel: null, gammaWater: ENGINE_DEFAULTS.water.gammaWater },
    loads: { surcharge: 10, lineLoads: [], selfWeightFactorSeismic: 1.0 },
    seismic: {
      enabled: true, inputMode: "map", level: ENGINE_DEFAULTS.seismic.level,
      SS: 1.0, S1: 0.25, siteClass: "ZC",
      r: ENGINE_DEFAULTS.seismic.r, saturatedSoilLimitsR: false,
      dynamicPointOfApplication: ENGINE_DEFAULTS.seismic.dynamicPointOfApplication,
      includeWallInertia: ENGINE_DEFAULTS.seismic.includeWallInertia,
      includeBackfillInertia: ENGINE_DEFAULTS.seismic.includeBackfillInertia,
      kvOnTotalPressure: ENGINE_DEFAULTS.seismic.kvOnTotalPressure,
    },
    material: stoneMaterial(DEFAULT_STONE_PRESET),
    checks: {
      classic: structuredClone(ENGINE_DEFAULTS.checks.classic),
      tbdy: { gammaRdev: ENGINE_DEFAULTS.checks.tbdy.gammaRdev, gammaRdevSeismic: ENGINE_DEFAULTS.checks.tbdy.gammaRdevSeismic, adhesionInSliding: false },
      includeActiveVerticalComponent: true,
      surchargeAsWeight: true,
      extraJointLevels: [],
      designCode: "tbdy2018",
      pressureMethod: "virtual-plane",   // iCad Rijit ile birebir doğrulanan yöntem (icad.test.ts); back-face seçenek (Das/ADÜ pratiği)
      ec: { approach: "DA1", nationalAnnex: "EN", psi2: 0.3 },
      classicGoverns: false,
    },
    globalStability: makeDefaultGlobalStability(),
    rc: makeDefaultRc(),
    quantities: makeDefaultQuantities(),
    drawing: makeDefaultDrawing(),
  };
}

/** Metraj varsayılanları: çalışma payı 0,5 m, kazı şevi 1/2, grobeton 10 cm, harç %30, donatı bindirme+fire %10, drenaj açık */
export function makeDefaultQuantities(): WallInput["quantities"] {
  return {
    workingSpace: 0.5, excavationSlope: 0.5, blindingThickness: 0.1, blindingOverhang: 0.1, mortarRatio: 0.3, stoneBulking: 1.25, wasteFactor: 0, steelExtra: 0.1,
    drainage: { enabled: true, pipeDiameter: 100, filterThickness: 0.3, barbacaneSpacing: 2, barbacaneDiameter: 100 },
  };
}

/** Çizim (DXF paftası) varsayılanları: cm, 1:50, taş dokusu ve barbakan/drenaj ölçüleri metrajdan; kotlar ön zemine göre (±0,00) */
export function makeDefaultDrawing(): WallInput["drawing"] {
  return {
    unit: "cm", scale: 50, stone: "auto", levelRef: "ground", levelDatum: 0,
    barbacane: { show: "auto", hSpacing: 3, startHeight: 0.5, angleDeg: 5 },
    drain: { show: "auto" }, coping: { enabled: true, height: 0.1, overhang: 0.05 }, anoLength: 10,
    sheet: { paper: "auto", orientation: "landscape", offsets: {}, hidden: [], edits: { overrides: {}, added: [], exploded: [], blocks: {} }, layers: [], dimStyle: { textMm: 2.5, arrowMm: 2.5, arrow: "closed" } },
  };
}

/** Betonarme kesit tasarımı varsayılanları: C25 / B420C, pas payı 5 cm, yönetmelik profilinden türetilen kod, otomatik donatı */
export function makeDefaultRc(): WallInput["rc"] {
  return { concrete: "C25", steel: "B420C", cover: 0.05, code: "auto", stemLevelStep: 0.5, heelUplift: true, bars: {} };
}

/** Toptan göçme varsayılanları: kapalı; Bishop; otomatik ızgara; hedef 1,5 / 1,1 (TBDY 16.6.2 / 16.12.1.4 pratiği) */
export function makeDefaultGlobalStability(): WallInput["globalStability"] {
  return {
    enabled: false, method: "bishop",
    searchGrid: { auto: true, xRange: [-2, 8], yRange: [4, 14], step: 0.5 },
    minDepthBelowBase: 0.5, slices: 30, seismic: "pseudo-static",
    targets: { static: 1.5, seismic: 1.1 },
  };
}

export function makeDefaultProject(info: Partial<ProjectInfo>): Project {
  const now = new Date().toISOString();
  return {
    schemaVersion: 1,
    id: newId("prj"),
    info: { name: "Yeni Proje", date: now.slice(0, 10), revision: "R0", ...info },
    walls: [makeDefaultWall("Duvar-1")],
    reportSettings: {
      preset: "yeterli",
      sections: {
        cover: true, projectInfo: true, inputs: true, drawing: true, forceDiagram: true, seismicParams: true, coefficients: true,
        forces: true, steps: false, stabilityTbdy: true, stabilityClassic: true, basePressure: true, bearingEc8: true, joints: true, warnings: true,
        summary: true, references: true, globalStability: false, rcDesign: true, reinforcement: true, quantities: true,
      },
      wallIds: "all",
    },
    pricing: makeDefaultPricing(),
    createdAt: now,
    updatedAt: now,
  };
}
